import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useMemo } from 'react';

import { useAbTestingSdk } from '@sravni/react-utils';

import { useGetWlPageAffClickId } from '@src/hooks/usePixel/hooks';
import type { IMakePixelProps } from '@src/hooks/usePixel/makePixelLink';
import { makePixelState, updatePixelState } from '@src/hooks/usePixel/makePixelLink';
import { useSelectUser } from '@src/reducers/user';
import { isWhiteLabelPage } from '@src/utils/routing';

type AdvertisingContextProps = {
  getPixelLink(link: string, props: Partial<IMakePixelProps>): string;
};

const AdvertisingContext = createContext<AdvertisingContextProps | null>(null);

export function AdvertisingProvider({ children }: { children: ReactNode }) {
  const user = useSelectUser();
  const { asPath, pathname, query } = useRouter();
  const isWhiteLabel = isWhiteLabelPage(asPath);
  const affClickIdForWhiteLabel = useGetWlPageAffClickId();
  const abTestingSdk = useAbTestingSdk();

  const pixelData = useMemo(
    () =>
      makePixelState({
        pathname: asPath,
        userId: user?.sub,
        isWL: isWhiteLabel,
        route: pathname,
        abTestingSdkExperiments: abTestingSdk.getAllUserExperiments(),
        affClickId: affClickIdForWhiteLabel,
        query,
      }),
    [abTestingSdk, affClickIdForWhiteLabel, asPath, isWhiteLabel, pathname, query, user?.sub],
  );

  const getPixelLink = useCallback(
    (link, props: Partial<IMakePixelProps>) => updatePixelState(link, pixelData, props),
    [pixelData],
  );

  const contextData = React.useMemo(
    () => ({
      getPixelLink,
    }),
    [getPixelLink],
  );
  return <AdvertisingContext.Provider value={contextData}>{children}</AdvertisingContext.Provider>;
}
export function useAdvertisingPixel() {
  return useContext(AdvertisingContext);
}
